import apiService from './api'
import type { 
  ScanResult, 
  ScanRequest, 
  Finding,
  ScanProgress,
  ScanFilters,
  SortOptions,
  ExportOptions
} from '@/types/scan'
import type { ApiResponse, PaginatedResponse } from '@/types/api'

interface ScanListParams {
  page?: number
  per_page?: number
  filters?: ScanFilters
  sort?: SortOptions
}

class ScanService {
  private unwrap<T>(response: ApiResponse<T> | T): T {
    return response && typeof response === 'object' && 'data' in response
      ? (response as ApiResponse<T>).data
      : response as T
  }

  async createScan(request: ScanRequest): Promise<ScanResult> {
    const response = await apiService.post<ApiResponse<ScanResult> | ScanResult>('/scans', request)
    return this.unwrap(response)
  }

  async getScans(params: ScanListParams = {}): Promise<PaginatedResponse<ScanResult>> {
    const queryParams = new URLSearchParams()
    
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.per_page) queryParams.append('per_page', params.per_page.toString())
    
    // Add filters
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach((v: string) => queryParams.append(key, v))
          } else {
            queryParams.append(key, value.toString())
          }
        }
      })
    }
    
    // Add sorting
    if (params.sort) {
      queryParams.append('sort_by', params.sort.field)
      queryParams.append('sort_direction', params.sort.direction)
    }

    const url = `/scans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await apiService.get<ApiResponse<PaginatedResponse<ScanResult>> | PaginatedResponse<ScanResult>>(url)
    return this.unwrap(response)
  }

  async getScan(scanId: string): Promise<ScanResult> {
    const response = await apiService.get<ApiResponse<ScanResult> | ScanResult>(`/scans/${scanId}`)
    return this.unwrap(response)
  }

  async getScanStatus(scanId: string): Promise<ScanProgress> {
    const response = await apiService.get<ApiResponse<ScanProgress> | ScanProgress>(`/scans/${scanId}/status`)
    return this.unwrap(response)
  }

  async cancelScan(scanId: string): Promise<void> {
    await apiService.post(`/scans/${scanId}/cancel`)
  }

  async deleteScan(scanId: string): Promise<void> {
    await apiService.delete(`/scans/${scanId}`)
  }

  async exportScan(scanId: string, options: ExportOptions): Promise<void> {
    const queryParams = new URLSearchParams({
      format: options.format,
      include_findings: options.include_findings.toString(),
    })

    if (options.severity_filter?.length) {
      options.severity_filter.forEach((severity: string) =>
        queryParams.append('severity', severity)
      )
    }

    const filename = `scan-${scanId}-export.${options.format}`
    await apiService.download(`/scans/${scanId}/export?${queryParams.toString()}`, filename)
  }

  async getFindings(scanId: string, filters?: Partial<Finding>): Promise<Finding[]> {
    const queryParams = new URLSearchParams()

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString())
        }
      })
    }

    const url = `/scans/${scanId}/findings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await apiService.get<ApiResponse<Finding[]>>(url)
    return response.data
  }

  async updateFinding(scanId: string, findingId: string, updates: Partial<Finding>): Promise<Finding> {
    const response = await apiService.patch<ApiResponse<Finding>>(
      `/scans/${scanId}/findings/${findingId}`,
      updates
    )
    return response.data
  }

  async dismissFinding(scanId: string, findingId: string, reason?: string): Promise<void> {
    await apiService.post(`/scans/${scanId}/findings/${findingId}/dismiss`, { reason })
  }

  async bulkDismissFindings(scanId: string, findingIds: string[], reason?: string): Promise<void> {
    await apiService.post(`/scans/${scanId}/findings/bulk-dismiss`, {
      finding_ids: findingIds,
      reason,
    })
  }

  // Analytics and statistics
  async getScanStats(timeRange?: string): Promise<{
    total_scans: number
    completed_scans: number
    failed_scans: number
    total_findings: number
    critical_findings: number
    high_findings: number
    average_risk_score: number
    scan_trend: Array<{ date: string; count: number }>
    findings_by_severity: Array<{ severity: string; count: number }>
    findings_by_scanner: Array<{ scanner: string; count: number }>
  }> {
    const days = timeRange === '7d' ? 7 : timeRange === '90d' ? 90 : 30
    const response = await apiService.get<ApiResponse<any> | any>(`/scans/stats/summary?days=${days}`)
    return this.unwrap(response)
  }

  async getRepositoryStats(repositoryUrl: string): Promise<{
    total_scans: number
    latest_scan: ScanResult | null
    risk_trend: Array<{ date: string; risk_score: number }>
    common_findings: Array<{ title: string; count: number }>
  }> {
    const queryParams = new URLSearchParams({ repository_url: repositoryUrl })
    const response = await apiService.get<ApiResponse<any>>(`/repository/stats?${queryParams.toString()}`)
    return response.data
  }
}

export const scanService = new ScanService()
export default scanService
